import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchTicketsSold, downloadTicketsReport } from '../api/ticketsApi';
import { Download } from 'lucide-react';
import TicketStats from './TicketStats';
import AirlineChart from './AirlineChart';
import AirlineTable from './AirlineTable';
import DateRangeSelector from './DateRangeSelector';
import MetadataExplorer from './MetadataExplorer';
import './Dashboard.css';
import config from '../config';

const { environment } = config;

function Dashboard({ accessToken }) {
  const [searchParams, setSearchParams] = useSearchParams();
  //const navigate = useNavigate(); // For Historical Data navigation

  const ENVIRONMENTS = [process.env.REACT_APP_ENVIRONMENT];
  const DEFAULT_ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;

  const [selectedEnvironment, setSelectedEnvironment] = useState(() => {
    return searchParams.get('environment') || DEFAULT_ENVIRONMENT;
  });
  const [ticketsData, setTicketsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRangeOption, setDateRangeOption] = useState('current-month');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1);
    return date;
  });
  const [endDate, setEndDate] = useState(() => new Date());
  const [downloadLoading, setDownloadLoading] = useState(false);

  const getDateRangeForOption = (option) => {
    let start, end;
    const today = new Date();
    const utcMidnight = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));

    switch (option) {
      case 'current-month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = utcMidnight;
        break;
      case 'previous-month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'custom':
        return { start, end };
      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    }
    return { start, end };
  };

  useEffect(() => {
    if (dateRangeOption !== 'custom') {
      const { start, end } = getDateRangeForOption(dateRangeOption);
      setStartDate(start);
      setEndDate(end);
    }
  }, [dateRangeOption]);

  const loadTicketsData = useCallback(async () => {
    debugger
    if (!accessToken) {
      setError('No access token available');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTicketsSold(
        startDate,
        endDate,
        accessToken,
        selectedEnvironment,
        dateRangeOption
      );
      setTicketsData(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch tickets data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, accessToken, selectedEnvironment, dateRangeOption]);

  useEffect(() => {
    loadTicketsData();
  }, [loadTicketsData]);

  const handleDateRangeChange = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleDateRangeOptionChange = (option) => {
    setDateRangeOption(option);
    if (option === 'custom') {
      const today = new Date();
      setStartDate(today);
      setEndDate(today);
    }
  };

  const handleDownloadReport = useCallback(async () => {
    if (!accessToken) {
      setError('No access token available');
      return;
    }
    setDownloadLoading(true);
    try {
      await downloadTicketsReport(startDate, endDate, accessToken, selectedEnvironment, dateRangeOption);
    } catch (err) {
      setError(err.message || 'Failed to download report');
      console.error('Download error:', err);
    } finally {
      setDownloadLoading(false);
    }
  }, [startDate, endDate, accessToken, selectedEnvironment, dateRangeOption]);

  return (
    <section className="dashboard">
      {error && (
        <div className="dashboard-error">
          <p>Error: {error}</p>
          <button className="btn btn-danger" onClick={loadTicketsData}>
            Retry
          </button>
        </div>
      )}

      <div className="ticket-airline-card card clearfix"> 

        {!loading && (
        <div className="dashboar-data-filter w-full"> 

          <div className="date-range-options"> 
            <div className="data-filter-button radio-buttons-group d-inline-flex">
              <div className="radio-custom radio-custom-button">
                <input
                  type="radio"
                  name="dateRange"
                  id="current-month"
                  value="current-month"
                  checked={dateRangeOption === 'current-month'}
                  onChange={(e) => handleDateRangeOptionChange(e.target.value)}
                />
                <label htmlFor="current-month" className="radio-label d-flex align-items-center">
                  Current Month
                </label>
              </div>
              <div className="radio-custom radio-custom-button">
                <input
                  type="radio"
                  name="dateRange"
                  id="previous-month"
                  value="previous-month"
                  checked={dateRangeOption === 'previous-month'}
                  onChange={(e) => handleDateRangeOptionChange(e.target.value)}
                />
                <label htmlFor="previous-month" className="radio-label d-flex align-items-center">
                  Previous Month
                </label>
              </div>
              <div className="radio-custom radio-custom-button">
                <input
                  type="radio"
                  name="dateRange"
                  id="custom"
                  value="custom"
                  checked={dateRangeOption === 'custom'}
                  onChange={(e) => handleDateRangeOptionChange(e.target.value)}
                />
                <label htmlFor="custom" className="radio-label d-flex align-items-center">
                  Custom Date Range
                </label>
 

            </div>
             </div>
    

          </div>

          {dateRangeOption === 'custom' && (
            <DateRangeSelector startDate={startDate} endDate={endDate} today={new Date()} onDateRangeChange={handleDateRangeChange} />
          )}
        </div> 

        )}
    
        {loading && (
          <div className="loader-wrap w-full justify-content-center">
            <div className="spinner"></div>
            <p className="text text-center">Loading tickets data...</p>
          </div>
        )}

        {ticketsData && !loading && (
          <div className="dashboard-content">
            <div className="section-header w-full d-lg-flex flex-wrap justify-content-between">
              <div className="button-group text-right">
                <button
                  className="btn btn-download"
                  disabled={downloadLoading}
                  title="Download tickets report as PDF"
                  onClick={handleDownloadReport}
                >
                <Download size={18}/>  {downloadLoading ? 'Downloading...' : 'Download'}
                </button>

             
              </div>

              <h2 className="section-title">Tickets Sold by Airlines</h2>
            </div>

            <div className="airlineTicketsTable">
              <AirlineTable data={ticketsData.countByAirlines} />
              <TicketStats data={ticketsData} />
            </div>

            <AirlineChart data={ticketsData.countByAirlines} />
          </div>
        )}
      </div>

      {/* <MetadataExplorer accessToken={accessToken} environments={ENVIRONMENTS} /> */}
      
    </section>
  );
}

export default Dashboard;